
import BCXBreadcrumbs from '@/components/molecules/BCXBreadcrumbs.vue';
import BCXInfoSection from '@/components/molecules/BCXInfoSection.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXUserBanner from '@/components/molecules/BCXUserBanner.vue';
import ClosedStatusIcon from '@/assets/svg/checkmark_s3.svg?inline';
import Eye from '@/components/svg-components/Eye.vue';
import TagList from '@/components/tagging/TagList.vue';
import Voting from '@/components/voting/Voting.vue';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import BCXHTMLSanitizer from '@/utils/BCXHTMLSanitizer';
import CreateMessage from '@/views/forum/components/CreateMessage.vue';
import ThreadTopicTitle from '@/views/forum/components/ThreadTopicTitle.vue';
import { MessagePost } from '@/views/forum/models/Forum';
import InnerSection from '@/views/project-forum/components/InnerSection.vue';
import ProjectForumSection from '@/views/project-forum/components/ProjectForumSection.vue';
import ProjectForumTopicSkeleton from '@/views/project-forum/components/ProjectForumTopicSkeleton.vue';
import { PostProject, Project } from '@/views/project-forum/models/ProjectForum';
import { truncate } from 'lodash';
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  toRef,
  watch,
  Ref
} from 'vue';
import { InfoBoxType } from '@/models/InfoBox';
import ProjectActionsButtons from '@/views/project-forum/components/sidebar-actions/ProjectActionsButtons.vue';
import { is } from '@babel/types';
import ProjectActionLayers from '@/views/project-forum/components/sidebar-actions/ProjectActionLayers.vue';
import useUser from '@/mixins/useUser';
import { useStore } from 'vue2-helpers/vuex';
import { Profile } from '@/models/Profile';
import { getProfile } from '@/services/Profile/Api';
import { Industry } from '@/models/Tags';
import useProjectGuestState from '@/state/projectGuestState';
import { CompanyGuestListResponse } from '@/models/Company';
import BCXUserListItem from '@/components/molecules/BCXUserListItem.vue';
import useI18n from '@/mixins/useI18n';
import MyProjectStatusLayer from '@/views/my-projects/components/offers/MyProjectStatusLayer.vue';
import { useMyProjectsProvider } from '@/views/my-projects/composables/useMyProjects';
import useResponsiveness from '@/mixins/useResponsiveness';
import { whenever } from '@vueuse/core';
import { UserRoles } from '@/models/User';
import ProjectAdminInfo from '@/views/project-forum/components/admin-info/ProjectAdminInfo.vue';
import ProjectViewersPopup from '@/views/project-forum/components/viewers/ProjectViewersPopup.vue';

export default defineComponent({
  name: 'ProjectForumTopic',
  components: {
    ProjectActionLayers,
    ProjectActionsButtons,
    BCXInfoSection,
    BCXBreadcrumbs,
    ThreadTopicTitle,
    Eye,
    Voting,
    ProjectForumSection,
    InnerSection,
    ProjectForumTopicSkeleton,
    BCXUserBanner,
    ClosedStatusIcon,
    CreateMessage,
    TagList,
    BCXMarkdown,
    BCXUserListItem,
    MyProjectStatusLayer,
    ProjectAdminInfo,
    ProjectViewersPopup
  },
  props: {
    project: {
      type: Object as () => Project,
      required: true,
      default: () => ({} as Project)
    },
    threadCreator: {
      type: Object,
      default() {
        return { userId: '' };
      }
    },
    created: {
      type: String,
      default: '2022-01-01T20:20:00.000Z'
    }
  },
  emits: ['post-message'],
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const { tUserFallback, isGuest, isAdmin } = useUser('self');
    const currentUser = useUser('self');
    const { isMobileWidth, isMobileLayout } = useResponsiveness();
    const project = toRef(props, 'project') as Ref<Project>;
    const profile = ref<Profile>();
    const isHelpOpen = ref(false);
    const projectDescriptionEl = ref<HTMLElement>();

    // Reactive data
    const noForumReplies = ref(false);
    const isOpen = ref(true);
    const createMessageVisible = ref(false);
    const projectDescriptionIsExpandable = ref(false);
    const isRecommendationOpen = ref(false);
    const isApplianceOpen = ref(false);
    const openedLayerType = ref<'' | 'apply' | 'recommend'>('');
    const isViewersPopupOpen = ref(false);

    // Store getters and state
    const user = computed(() => store.getters.user);
    const isSidebarVisible = computed(() => store.getters['page/isSidebarVisible']);
    const isSmartphoneWidth = computed(() => store.state.page.isSmartphoneWidth);
    const getProjectThreadNumberOfViews = computed(() => store.getters['projectForum/getProjectThreadNumberOfViews']);
    const cities = computed(() => store.state.projectForum.cities);
    const countries = computed(() => store.state.projectForum.countries);
    const industries = computed(() => store.state.projectForum.industries);
    const thread = computed(() => store.state.projectForum.thread);
    const guests = computed(() => store.state.projectForum.guests);
    const createMessageTrigger = computed(() => store.state.projectForum.createMessageTrigger);

    const { companyId, projectList, projectGuestList } = useProjectGuestState();
    const activeProjectGuests = computed(() => projectGuestList.value.filter((guest) => guest.active ?? false));

    const getGuestDisplayName = (guest: CompanyGuestListResponse) => {
      const guestUser = useUser(guest);
      return String(guestUser.displayName.value);
    };

    // Store actions
    const fetchCountries = () => store.dispatch('projectForum/fetchCountries');
    const fetchCities = (payload: any) => store.dispatch('projectForum/fetchCities', payload);
    const fetchIndustries = () => store.dispatch('projectForum/fetchIndustries');
    const updateProject = (project: PostProject) => store.dispatch('projectForum/updateProject', project);
    const hideProject = () => store.dispatch('projectForum/hideProject', project.value.id);
    const showProject = () => store.dispatch('projectForum/showProject', project.value.id);

    // Computed properties
    const breadcrumbItems = computed(() => {
      const breadcrumbItems: Array<any> = [
        {
          text: tUserFallback('project-forum_USERTYPE.labels.projects'),
          to: { name: 'projectList' }
        },
        {
          text: project.value.title
        }
      ];
      if (isMobileLayout.value) {
        breadcrumbItems.unshift({ text: 'ByteCookie' });
        breadcrumbItems[2].text = truncate(project.value.title, { length: 32 });
      }
      return breadcrumbItems;
    });

    const isProjectClosed = computed(() => project.value.status !== 'ACTIVE' && project.value.status !== 'HOLD');
    const initialMessageId = computed(() => thread.value.initialMessage.messageId);
    const flagged = computed(() => project.value.userMetaDataProject.flagged || false);
    const voteResult = computed(() => project.value.metaDataVoting.voteResult);
    const userVoting = computed(() => project.value.userMetaDataVoting.userVoted);
    const messageEdited = computed(() => thread.value.initialMessage.edited);
    const status = computed(() => String(tUserFallback(`project-forum_USERTYPE.labels.status.${project.value.status}`)));
    const projectDescription = computed(() => project.value.descriptionLong ?? '');
    const userIsCreator = computed(() => user.value.userId === project.value.creator.userId);

    const showProjectGuestSection = computed(() => {
      const hasCreatorAndCompany = userIsCreator.value && (project.value.company?.id?.length ?? 0) > 0;
      const hasActiveGuests = Array.isArray(activeProjectGuests.value) && activeProjectGuests.value.length > 0;
      return hasCreatorAndCompany || hasActiveGuests;
    });

    const showOpenCloseProject = computed(() => userIsCreator.value
      || user.value.roles.includes('PROJECT_MODERATOR')
      || user.value.roles.includes('FORUM_MODERATOR'));

    const userCanEdit = computed(() => userIsCreator.value
      || user.value.roles.includes('FORUM_MODERATOR')
      || user.value.roles.includes('PROJECT_MODERATOR'));

    const userCanHideProject = computed(() => user.value.roles.includes('ADMIN')
      || user.value.roles.includes('PROJECT_MODERATOR'));

    const isProjectHidden = computed(() => Boolean(project.value?.hidden));

    const backLink = computed(() => ({ name: 'projectList' }));

    const createMessageAttachedTo = computed(() => {
      if (isMobileLayout.value) {
        return `#${(document.querySelector('#app') as HTMLElement)?.id}`;
      }
      return '.create-message-block';
    });

    const statusIndicatorClass = computed(() => {
      if (project.value.status === 'ACTIVE') return 'project-status--open';
      if (project.value.status === 'HOLD') return 'project-status--hold';
      return 'project-status--closed';
    });

    const avatarSize = computed(() => (isMobileLayout.value ? 50 : 60));

    const createdTimestamp = computed(() => {
      const { getFormattedTimeByAge } = useFormattedDateTime();
      return getFormattedTimeByAge(props.created as string);
    });

    const clientValue = computed(() => ({
      countryCode: project.value.city.countryCode,
      placeName: project.value.city.placeName,
      postalCode: project.value.city.postalCode,
      initialIndustry: project.value.industryTagId,
      countryList: countries.value,
      cityList: cities.value,
      initialCompanySize: project.value.companySize,
      industryList: industries.value
    }));

    const conditionsValue = computed(() => ({
      locationValue: project.value.projectLocation,
      remoteWorkValue: project.value.remoteWork,
      teamSizeValue: project.value.teamSize,
      startDateValue: project.value.startDate,
      durationAmountValue: project.value.durationAmount,
      durationUnitValue: project.value.durationUnit,
      hoursPerWeekValue: project.value.hoursPerWeek,
      dependingOnComplexityValue: project.value.dependingOnComplexity
    }));

    const innerSectionsClient = computed(() => {
      const sections: Array<{ title: string; text: string; break?: boolean }> = [];
      if (project.value.city.placeName) {
        sections.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.client.office.label')),
          text: project.value.city.placeName
        });
      } else if (profile.value?.basic.city) {
        sections.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.client.office.label')),
          text: profile.value.basic.city
        });
      }
      if (project.value.companySize) {
        sections.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.client.company-size.label')),
          text: String(tUserFallback(`project-forum_USERTYPE.form-section.client.company-size.dropdown.${project.value.companySize}`))
        });
      }
      if (project.value.industryTagId) {
        const industry = industries.value.find((industry: Industry) => industry.externalId === project.value.industryTagId);
        sections.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.client.industry.label')),
          text: industry?.label ?? ''
        });
      }
      return sections;
    });

    const innerSectionsConditions = computed(() => {
      const projectDurationUnitText = String(tUserFallback(`project-forum_USERTYPE.form-section.conditions.duration.dropdown.${project.value.durationUnit}`));
      const projectConditions: Array<{ title: string; text: string }> = [];

      if (project.value.teamSize) {
        projectConditions.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.team-size.label')),
          text: String(project.value.teamSize)
        });
      }
      if (project.value.startDate) {
        const { getFormattedDate } = useFormattedDateTime();
        projectConditions.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.start-date.label')),
          text: getFormattedDate(project.value.startDate)
        });
      }
      if (project.value.durationAmount && project.value.durationUnit) {
        projectConditions.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.duration.label')),
          text: `${project.value.durationAmount} ${projectDurationUnitText}`
        });
      }
      if (project.value.hoursPerWeek) {
        projectConditions.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.hours-per-week.label')),
          text: String(project.value.hoursPerWeek)
        });
      }
      if (project.value.projectLocation) {
        projectConditions.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.location.label')),
          text: String(tUserFallback(`project-forum_USERTYPE.form-section.conditions.location.dropdown.${project.value.projectLocation}`))
        });
      }
      if (project.value.remoteWork && project.value.projectLocation === 'ONSITE_AND_REMOTE') {
        projectConditions.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.remote-work.label')),
          text: String(tUserFallback(`project-forum_USERTYPE.form-section.conditions.remote-work.values.${project.value.remoteWork}`))
        });
      }
      if (project.value.dependingOnComplexity) {
        projectConditions.push({
          title: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.hours-per-week.label')),
          text: String(tUserFallback('project-forum_USERTYPE.form-section.conditions.depending-on-complexity.value'))
        });
      }
      return projectConditions;
    });

    const titleText = computed(() => {
      if (project.value.metaDataProject.testFlag) {
        return `[TEST] ${project.value.title}`;
      }
      return project.value.title;
    });

    const isTestProject = computed(() => project.value.metaDataProject.testFlag || false);

    const messageTextTemplate = computed(() => String(tUserFallback('project-forum_USERTYPE.templates.reply-comment')));

    const createMessagePlaceHolder = computed(() => {
      if (!store.state.projectForum.project.id.length) {
        return '';
      }
      if (currentUser.userId.value === store.state.projectForum.project.creator.userId
        || projectGuestList.value.map((guest) => guest.id).includes(currentUser.userId.value)) {
        return String(t('project-forum.placeholder.project-answer-op'));
      }
      return String(t('project-forum.placeholder.project-answer-user'));
    });

    // Watchers
    watch(
      () => project.value.creator.userId,
      async () => {
        if (!profile.value) {
          profile.value = await getProfile(project.value.creator.userId ?? '');
        }
      }
    );

    watch(
      () => project.value.company?.id,
      async (newVal) => {
        if (newVal) companyId.value = newVal;
      }
    );

    watch(
      () => project.value.id,
      (newVal) => {
        if (newVal && !projectList.value.includes(newVal)) projectList.value = [newVal];
      }
    );

    watch(createMessageTrigger, () => {
      createMessageVisible.value = true;
      const messageBlock = document.querySelector<HTMLElement>('.create-message-block');
      if (!messageBlock) return;
      messageBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    watch(isSidebarVisible, (isVisible) => {
      if (isVisible) openedLayerType.value = '';
    });

    const { isStatusLayerOpen, statusLayerProject, reloadAfterProjectChange } = useMyProjectsProvider({
      isNarrow: computed(() => isMobileWidth.value)
    });

    // Methods
    const setDescriptionExpandable = () => {
      if (projectDescriptionEl.value && projectDescriptionEl.value.clientHeight > 90) {
        projectDescriptionIsExpandable.value = true;
      }
    };

    const formatDate = (timestamp: string, dateformat: 'short' | 'diff' = 'diff'): string => {
      const { getFormattedTimeByAge, getFormattedDate } = useFormattedDateTime();
      if (dateformat === 'diff') return getFormattedTimeByAge(timestamp);
      return getFormattedDate(timestamp);
    };

    const toggleCreateMessageVisible = () => {
      createMessageVisible.value = !createMessageVisible.value;
    };

    const saveEdit = (updateObject: any) => {
      const projectData: PostProject = {
        id: project.value.id,
        title: project.value.title,
        descriptionLong: project.value.descriptionLong.trim(),
        status: project.value.status,
        testFlag: project.value.metaDataProject.testFlag,
        type: project.value.type,
        paymentType: project.value.paymentType,
        cityPostalCode: project.value.city.postalCode,
        cityPlacename: project.value.city.placeName,
        countryId: project.value.city.countryCode,
        companySize: project.value.companySize,
        hourlyRateInEUR: project.value.hourlyRateInEUR,
        startDate: project.value.startDate,
        startDateLatest: project.value.startDateLatest,
        optionToExtendDuration: project.value.optionToExtendDuration,
        remotePercentage: project.value.remotePercentage,
        companyId: '',
        projectLocation: project.value.projectLocation,
        remoteWork: project.value.remoteWork,
        teamSize: project.value.teamSize,
        industryTagId: project.value.industryTagId,
        durationAmount: project.value.durationAmount,
        durationUnit: project.value.durationUnit,
        hoursPerWeek: project.value.hoursPerWeek,
        dependingOnComplexity: project.value.dependingOnComplexity,
        ...updateObject
      };
      updateProject(projectData);
    };

    const saveTitle = (data: { title: string; testFlag: boolean }) => {
      saveEdit({ ...data });
    };

    const saveDescription = (description: string) => {
      saveEdit({ descriptionLong: BCXHTMLSanitizer.beforeSave(description) });
    };

    const saveClient = (clientData: any) => {
      saveEdit({
        cityPlacename: clientData.cityPlaceName,
        countryId: clientData.countryCode,
        cityPostalCode: clientData.cityPostalCode,
        companySize: clientData.companySize,
        industryTagId: clientData.industry
      });
    };

    const saveConditions = (conditions: any) => {
      if (conditions.durationAmount === '') conditions.durationAmount = 0;
      if (conditions.hoursPerWeek === '') conditions.hoursPerWeek = 0;
      if (conditions.teamSize === '') conditions.teamSize = 0;

      saveEdit({
        durationAmount: conditions.durationAmount,
        durationUnit: conditions.durationUnit,
        projectLocation: conditions.location,
        remoteWork: conditions.remoteWork,
        teamSize: conditions.teamSize,
        startDate: conditions.startDate,
        hoursPerWeek: conditions.hoursPerWeek,
        dependingOnComplexity: conditions.dependingOnComplexity
      });
    };

    const closeProject = () => {
      statusLayerProject.value = project.value;
      isStatusLayerOpen.value = true;
    };

    const reopenProject = () => {
      saveEdit({ status: 'ACTIVE' });
    };

    const postMessage = (message: string) => {
      const postMessage: MessagePost = {
        forumId: thread.value.forumId,
        forumThreadId: thread.value.threadId,
        messageContent: message,
        replyToMessageId: thread.value.initialMessage.messageId,
        replyToUserId: String(project.value.creator?.userId)
      };
      emit('post-message', postMessage);
      createMessageVisible.value = false;
    };

    const recommendProject = () => {
      if (!isSidebarVisible.value) {
        openedLayerType.value = 'recommend';
      } else {
        store.dispatch('projectForum/triggerRecommend');
      }
    };

    const applyToProject = () => {
      if (!isSidebarVisible.value) {
        openedLayerType.value = 'apply';
      } else {
        store.dispatch('projectForum/triggerApply');
      }
    };

    whenever(() => !isStatusLayerOpen.value, () => {
      statusLayerProject.value = null;
    });

    // Lifecycle
    onMounted(async () => {
      if (project.value.creator.userId) {
        profile.value = await getProfile(project.value.creator.userId);
      }
      if (project.value.company?.id) companyId.value = project.value.company.id;
      if (project.value.id) projectList.value = [project.value.id];

      nextTick(() => {
        fetchCountries().then(() => {
          fetchCities({ countryId: project.value.city.countryCode, searchString: project.value.city.placeName });
        });
        fetchIndustries();
        setTimeout(() => {
          setDescriptionExpandable();
        }, 200);
      });

      window.addEventListener('resize', () => {
        setDescriptionExpandable();
      });

      store.subscribeAction({
        after: (action) => {
          if (action.type === 'projectForum/toggleMessage') console.log('Action:', action);
        }
      });
    });

    return {
      // Refs
      projectDescriptionEl,

      // Reactive data
      noForumReplies,
      isOpen,
      createMessageVisible,
      projectDescriptionIsExpandable,
      isRecommendationOpen,
      isApplianceOpen,
      openedLayerType,
      isViewersPopupOpen,
      InfoBoxType,

      // Computed from setup
      isHelpOpen,
      displayInfobox: computed(() => isGuest.value),
      tUserFallback,
      isAdmin,
      innerSectionsClient,
      activeProjectGuests,
      getGuestDisplayName,
      createMessagePlaceHolder,

      // Store computed
      user,
      isMobileLayout,
      isSidebarVisible,
      getProjectThreadNumberOfViews,
      cities,
      countries,
      industries,
      thread,
      guests,
      createMessageTrigger,
      isSmartphoneWidth,

      // Component computed
      breadcrumbItems,
      isProjectClosed,
      initialMessageId,
      flagged,
      voteResult,
      userVoting,
      messageEdited,
      status,
      projectDescription,
      userIsCreator,
      showProjectGuestSection,
      showOpenCloseProject,
      userCanEdit,
      userCanHideProject,
      isProjectHidden,
      backLink,
      createMessageAttachedTo,
      statusIndicatorClass,
      avatarSize,
      createdTimestamp,
      clientValue,
      conditionsValue,
      innerSectionsConditions,
      titleText,
      isTestProject,
      messageTextTemplate,

      // MyProjects provider
      isStatusLayerOpen,
      statusLayerProject,

      // Methods
      is,
      setDescriptionExpandable,
      formatDate,
      toggleCreateMessageVisible,
      saveTitle,
      saveDescription,
      saveClient,
      saveConditions,
      closeProject,
      reopenProject,
      saveEdit,
      postMessage,
      recommendProject,
      applyToProject,
      hideProject,
      showProject,
    };
  }
});
